/**
 * Box 5 (spec §4.2, §5.5): each channel's mean response curve, the weekly outcome at each weekly budget on the
 * channel's own grid (0 to G, so nothing is extrapolated), in the bundle's order and colorAt(i) as box 3 colours them;
 * a stopped channel is dashed. On the mean curve, ● marks a running channel's current budget, and ○ the simulator's
 * figure wherever it moves the channel by MIN_MOVE or more (T9a: a move that prints 0.00 is none). A count outcome's
 * subtitle names its units and its curves print as counts; the budget axis stays money (spec C11).
 *
 * @module components/budget/ResponseCurves
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Legend, Line, LineChart, ReferenceDot, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { colorAt } from '@/components/charts/chartPalette';
import { AXIS_PROPS, CHART_HEIGHT, GRID_PROPS, LEGEND_PROPS, TOOLTIP_PROPS, compactTick } from '@/components/looker/charts/chartTheme';
import { OverviewCard } from '@/components/overview/OverviewBox';
import type { Bundle } from '@/services/budget/bundleSchema';
import { interpolate } from '@/services/budget/curves';
import { isCount, outcomeFigure, unitsName } from '@/services/budget/outcomeUnits';
import { MIN_MOVE, type Allocation } from '@/services/budget/simulator';
import type { TFn } from '@/services/channels/channelFormat';
import { fmtMoney } from '@/services/overview/overviewFormat';
import { platformShortLabel } from '@/utils/platformLabel';

/** The prototype's marks: r 5, a 2 px ring; the ● keeps a white edge where it overlaps its line. */
const MARK = { r: 5, strokeWidth: 2 } as const;

interface Point { x: number; y: number }

interface Curve {
  id: string;
  name: string;
  color: string;
  stopped: boolean;
  points: Point[];
}

const curvesOf = (b: Bundle, t: TFn): Curve[] =>
  b.channels.map((c, i) => {
    const { grid, mean } = b.curves[c.id];
    const label = platformShortLabel(c.id);
    return {
      id: c.id, color: colorAt(i), stopped: c.stopped,
      name: c.stopped ? t('budget.curves.stoppedName', { channel: label }) : label,
      points: grid.map((x, j) => ({ x, y: mean[j] })),
    };
  });

/** ● at current for a running channel; ○ at the simulator's figure when it moves the channel by MIN_MOVE or more. */
const marksOf = (b: Bundle, simulated: Allocation, c: Curve): React.ReactNode[] => {
  const { grid, mean } = b.curves[c.id];
  const now = b.current_weekly[c.id];
  // A channel the allocation leaves out spends 0, as the simulator reads it.
  const sim = simulated[c.id] ?? 0;
  return [
    !c.stopped && <ReferenceDot key={`${c.id}:now`} x={now} y={interpolate(grid, mean, now)} {...MARK} fill={c.color} stroke="#ffffff" />,
    Math.abs(sim - now) >= MIN_MOVE
      && <ReferenceDot key={`${c.id}:sim`} x={sim} y={interpolate(grid, mean, sim)} {...MARK} fill="none" stroke={c.color} />,
  ];
};

const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

interface Props {
  bundle: Bundle;
  /** The simulator's weekly allocation (box 6). */
  simulated: Allocation;
}

export const ResponseCurves: React.FC<Props> = ({ bundle, simulated }) => {
  const { t } = useTranslation();
  // The sliders redraw this box on every move; the curves change only with the bundle.
  const curves = useMemo(() => curvesOf(bundle, t), [bundle, t]);
  const { currency, outcome_metric: metric } = bundle;
  const figure = outcomeFigure(metric, currency);
  const subtitle = isCount(metric) ? t('budget.curves.subtitleCount', { units: unitsName(metric, t) }) : t('budget.curves.subtitle');
  return (
    <OverviewCard id="budget-5" title={t('budget.curves.title')}>
      <p className="-mt-2 mb-2 text-xs text-neutral-700">{subtitle}</p>
      <div style={{ height: CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis type="number" dataKey="x" {...AXIS_PROPS} tickFormatter={compactTick} />
            <YAxis {...AXIS_PROPS} tickFormatter={compactTick} />
            <Tooltip
              {...TOOLTIP_PROPS}
              labelFormatter={(x: unknown) => t('budget.curves.atBudget', { amount: fmtMoney(num(x), currency) })}
              formatter={(v: unknown) => figure(num(v))}
            />
            <Legend {...LEGEND_PROPS} />
            {curves.map((c) => (
              <Line
                key={c.id} data={c.points} dataKey="y" name={c.name} stroke={c.color} strokeWidth={2}
                strokeDasharray={c.stopped ? '5 4' : undefined} dot={false} isAnimationActive={false}
              />
            ))}
            {curves.flatMap((c) => marksOf(bundle, simulated, c))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-1 text-xs text-neutral-700">{t('budget.curves.axis', { currency })} · {t('budget.curves.marks')}</p>
      <p className="mt-2 text-xs text-neutral-700">{t('budget.curves.how')}</p>
    </OverviewCard>
  );
};
